import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  RequestLog,
  ClientMessage,
  ServerMessage,
  RequestMessage,
  HistoryMessage
} from '@mockd/shared';
import toast from 'react-hot-toast';
import { getEndpointWebSocketUrl } from '../config';
import { api, ApiError } from '../utils/apiClient';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface UseWebSocketOptions {
  subdomain?: string;
  endpointId?: string;
  projectId?: string;
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  status: ConnectionStatus;
  requests: RequestLog[];
  connect: () => void;
  disconnect: () => void;
  clearRequests: () => Promise<boolean>;
}

const MAX_REQUESTS = 100;
const PING_INTERVAL = 30000;
const MIN_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

function getWebSocketUrl(doName: string, ticket: string): string {
  const baseWsUrl = getEndpointWebSocketUrl();
  // Use path-based routing: /m/{doName}
  return `${baseWsUrl}/m/${doName}?ticket=${encodeURIComponent(ticket)}`;
}

// The socket lives on the endpoint worker, which can't see the session cookie,
// so the API mints a short-lived single-use ticket after checking access.
async function fetchLiveTicket(projectId: string, endpointId: string): Promise<string> {
  const res = await api.post<{ data: { ticket: string } }>(
    `/api/projects/${projectId}/endpoints/${endpointId}/live-ticket`,
  );
  return res.data.ticket;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { subdomain, endpointId, projectId, autoConnect = true } = options;
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [requests, setRequests] = useState<RequestLog[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectDelayRef = useRef(MIN_RECONNECT_DELAY);
  const shouldReconnectRef = useRef(true);
  // Bumped by every connect/disconnect, so a ticket that arrives after the
  // attempt that asked for it was superseded is dropped instead of used.
  const attemptRef = useRef(0);
  const connectRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const startPingInterval = useCallback(() => {
    pingIntervalRef.current = window.setInterval(() => {
      sendMessage({ type: 'ping' });
    }, PING_INTERVAL);
  }, [sendMessage]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as ServerMessage;

      switch (message.type) {
        case 'pong':
          // Keepalive acknowledged
          break;

        case 'request': {
          const requestMsg = message as RequestMessage;
          setRequests(prev => {
            const newRequests = [requestMsg.data, ...prev];
            return newRequests.slice(0, MAX_REQUESTS);
          });
          break;
        }

        case 'history': {
          const historyMsg = message as HistoryMessage;
          setRequests(prev => {
            const combined = [...historyMsg.data, ...prev];
            // Deduplicate by id
            const seen = new Set<string>();
            const unique = combined.filter(req => {
              if (seen.has(req.id)) return false;
              seen.add(req.id);
              return true;
            });
            return unique.slice(0, MAX_REQUESTS);
          });
          break;
        }

        case 'error':
          console.error('WebSocket error message:', message.data);
          break;
      }
    } catch (err) {
      console.error('Failed to parse WebSocket message:', err);
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!shouldReconnectRef.current) return;

    reconnectTimeoutRef.current = window.setTimeout(() => {
      connectRef.current();
    }, reconnectDelayRef.current);

    // Exponential backoff
    reconnectDelayRef.current = Math.min(
      reconnectDelayRef.current * 2,
      MAX_RECONNECT_DELAY
    );
  }, []);

  const connect = useCallback(async () => {
    // Don't connect without a valid subdomain/doName and the ids a ticket needs
    if (!subdomain || !projectId || !endpointId) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    clearTimers();
    shouldReconnectRef.current = true;
    setStatus('connecting');
    const attempt = ++attemptRef.current;

    try {
      const ticket = await fetchLiveTicket(projectId, endpointId);
      if (attempt !== attemptRef.current) return;

      const ws = new WebSocket(getWebSocketUrl(subdomain, ticket));
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        reconnectDelayRef.current = MIN_RECONNECT_DELAY;

        // The socket is scoped to endpointId by its ticket
        sendMessage({ type: 'getHistory' });

        startPingInterval();
      };

      ws.onmessage = handleMessage;

      ws.onerror = () => {
        setStatus('error');
      };

      ws.onclose = () => {
        setStatus('disconnected');
        clearTimers();
        scheduleReconnect();
      };
    } catch (err) {
      if (attempt !== attemptRef.current) return;
      console.error('Failed to create WebSocket:', err);
      setStatus('error');
      scheduleReconnect();
    }
  }, [clearTimers, endpointId, handleMessage, projectId, scheduleReconnect, sendMessage, startPingInterval, subdomain]);
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    attemptRef.current++;
    clearTimers();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('disconnected');
  }, [clearTimers]);

  // Resolves false (and leaves the list alone) when the server refused: clearing
  // only the local view would make the logs reappear on reload.
  const clearRequests = useCallback(async () => {
    if (projectId && endpointId) {
      try {
        await api.del(`/api/projects/${projectId}/endpoints/${endpointId}/logs`);
      } catch (err) {
        toast.error(err instanceof ApiError ? `Failed to clear logs: ${err.message}` : 'Failed to clear logs');
        return false;
      }
    }
    setRequests([]);
    return true;
  }, [projectId, endpointId]);

  // Auto-connect on mount or when subdomain/endpointId changes
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    status,
    requests,
    connect,
    disconnect,
    clearRequests,
  };
}
